export type Locale =
  | "en" | "ar" | "pl" | "uk" | "de" | "uz" | "th" | "hi" | "es" | "fr";

export interface LocaleMeta {
  code: Locale;
  /** Short badge shown in the language switcher (defaults to code) */
  label?: string;
  /** Native display name shown in the switcher */
  native: string;
  /** English name (used for AI translation prompts) */
  english: string;
  dir: "ltr" | "rtl";
  /** hreflang value */
  hreflang: string;
}

export const LOCALES: LocaleMeta[] = [
  { code: "en", native: "English", english: "English", dir: "ltr", hreflang: "en" },
  { code: "ar", native: "العربية", english: "Arabic", dir: "rtl", hreflang: "ar" },
  { code: "pl", native: "Polski", english: "Polish", dir: "ltr", hreflang: "pl" },
  { code: "uk", label: "UA", native: "Українська", english: "Ukrainian", dir: "ltr", hreflang: "uk" },
  { code: "de", native: "Deutsch", english: "German", dir: "ltr", hreflang: "de" },
  { code: "uz", native: "O‘zbekcha", english: "Uzbek (Latin script)", dir: "ltr", hreflang: "uz" },
  { code: "th", native: "ไทย", english: "Thai", dir: "ltr", hreflang: "th" },
  { code: "hi", native: "हिन्दी", english: "Hindi", dir: "ltr", hreflang: "hi" },
  { code: "es", native: "Español", english: "Spanish", dir: "ltr", hreflang: "es" },
  { code: "fr", native: "Français", english: "French", dir: "ltr", hreflang: "fr" },
];

export const DEFAULT_LOCALE: Locale = "en";

export const LOCALE_CODES = LOCALES.map((l) => l.code);

export function isLocale(value?: string | null): value is Locale {
  return !!value && (LOCALE_CODES as string[]).includes(value);
}

export function getLocaleMeta(code: Locale): LocaleMeta {
  return LOCALES.find((l) => l.code === code) ?? LOCALES[0];
}

/** Extracts the locale from a pathname such as /uk/blog */
export function localeFromPath(pathname: string): Locale {
  const seg = pathname.split("/")[1];
  return isLocale(seg) ? seg : DEFAULT_LOCALE;
}

/** Removes a locale prefix from a pathname */
export function stripLocale(pathname: string): string {
  const seg = pathname.split("/")[1];
  if (!isLocale(seg)) return pathname || "/";
  const rest = pathname.slice(seg.length + 1);
  return rest || "/";
}

/** Adds the locale prefix to a path (no prefix for English) */
export function withLocale(path: string, locale: Locale): string {
  const clean = stripLocale(path.startsWith("/") ? path : `/${path}`);
  if (locale === DEFAULT_LOCALE) return clean;
  return clean === "/" ? `/${locale}` : `/${locale}${clean}`;
}
